import React, { useState, useRef, useEffect, useCallback } from 'react';
import { importFromGitUrl, importFromZip, authFetch } from '../services/api';

// When projectId is provided: imports files INTO that existing project (chat context)
// When projectId is absent: creates a new project (home page context — currently unused)
export default function GitHubImport({ projectId, onImported, onCancel }) {
  const [tab, setTab] = useState('git');
  const [gitUrl, setGitUrl] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  // OAuth state
  const [oauthProvider, setOauthProvider] = useState(null);
  const [oauthToken, setOauthToken] = useState(null);
  const [repos, setRepos] = useState([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [importing, setImporting] = useState(null);

  const loadRepos = useCallback(async (provider, token) => {
    setOauthProvider(provider); setRepos([]); setRepoSearch('');
    setReposLoading(true);
    try {
      const res = await authFetch(`/api/oauth/${provider}/repos?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const detail = e.detail;
        throw new Error(typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Failed to load repos'));
      }
      const data = await res.json();
      setRepos(data.repos || []);
    } catch (e) { setError(e.message); }
    finally { setReposLoading(false); }
  }, []);

  useEffect(() => {
    const pollOAuthToken = async (provider, state) => {
      const maxAttempts = 40;
      for (let i = 0; i < maxAttempts; i++) {
        const res = await authFetch(`/api/oauth/${provider}/poll?state=${encodeURIComponent(state)}`);
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || 'Failed to retrieve token');
        }
        const result = await res.json();
        if (result.ready && result.token) return result.token;
        await new Promise(r => setTimeout(r, 500));
      }
      throw new Error('Timed out waiting for authentication. Please try again.');
    };

    const processOAuthResult = async (data) => {
      if (data.status === 'error') { setError(`OAuth failed: ${data.payload}`); return; }
      const state = data.payload;
      const provider = data.provider;
      if (!provider || !state) { setError('Invalid OAuth response'); return; }
      try {
        const token = await pollOAuthToken(provider, state);
        setOauthToken(token);
        loadRepos(provider, token);
      } catch (err) { setError(err.message); }
    };

    const handleMessage = (e) => {
      if (e.data?.type === 'oauth_callback') processOAuthResult(e.data);
    };

    const handleStorage = (e) => {
      if (e.key !== 'oauth_result') return;
      try {
        const data = JSON.parse(e.newValue);
        if (data?.type === 'oauth_callback') {
          localStorage.removeItem('oauth_result');
          processOAuthResult(data);
        }
      } catch (_) {}
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadRepos]);

  const disconnectProvider = async () => {
    if (oauthToken && oauthProvider) {
      await authFetch(`/api/oauth/${oauthProvider}/revoke?token=${encodeURIComponent(oauthToken)}`, { method: 'POST' }).catch(() => {});
    }
    setOauthProvider(null);
    setOauthToken(null);
    setRepos([]);
    setRepoSearch('');
    setError('');
  };

  const connectProvider = async (provider) => {
    setError('');
    try {
      const res = await authFetch(`/api/oauth/${provider}/authorize`);
      if (!res.ok) throw new Error('Failed to get auth URL');
      const { url, state } = await res.json();
      // Attach provider to the postMessage by storing it keyed to state
      window._oauthStateMap = window._oauthStateMap || {};
      window._oauthStateMap[state] = provider;
      window.open(url, `oauth_${provider}_${Date.now()}`, 'width=600,height=700,left=300,top=100');
    } catch (e) { setError(e.message); }
  };

  const handleOauthImport = async (repo) => {
    setImporting(repo.name); setError('');
    try {
      if (projectId) {
        const name = repo.name.split('/').pop();
        const data = await importFromGitUrl(repo.url, name, oauthToken);
        const res = await authFetch(`/api/projects/${data.project_id}/files`);
        const files = res.ok ? await res.json() : {};
        await authFetch(`/api/projects/${data.project_id}`, { method: 'DELETE' }).catch(() => {});
        if (!Object.keys(files).length) throw new Error('No files found.');
        const mergeRes = await authFetch(`/api/projects/${projectId}/import-files`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files }),
        });
        if (!mergeRes.ok) throw new Error('Failed to import files.');
        const mergeData = await mergeRes.json();
        onImported({ fileCount: Object.keys(files).length, changes: mergeData.changes });
      } else {
        const name = repo.name.split('/').pop();
        const data = await importFromGitUrl(repo.url, name, oauthToken);
        onImported(data.project_id, data);
      }
    } catch (e) { setError(e.message); }
    finally { setImporting(null); }
  };

  const handleImport = async () => {
    setError('');

    if (tab === 'git' && !gitUrl.trim()) { setError('Please enter a Git URL.'); return; }
    if (tab === 'zip' && !zipFile) { setError('Please select a .zip file.'); return; }

    setLoading(true);
    try {
      if (projectId) {
        // ── Import INTO existing project ──────────────────────────────────
        // Step 1: fetch files via existing import endpoints
        let files = {};
        if (tab === 'git') {
          const name = _repoNameFromUrl(gitUrl) || 'imported';
          const data = await importFromGitUrl(gitUrl.trim(), name);
          // data.project_id is a NEW project — fetch its files then merge into current
          const res = await authFetch(`/api/projects/${data.project_id}/files`);
          if (res.ok) files = await res.json();
          // Clean up the temp project (archive it silently)
          await authFetch(`/api/projects/${data.project_id}`, { method: 'DELETE' }).catch(() => {});
        } else {
          const data = await importFromZip(zipFile, 'temp-import');
          const res = await authFetch(`/api/projects/${data.project_id}/files`);
          if (res.ok) files = await res.json();
          await authFetch(`/api/projects/${data.project_id}`, { method: 'DELETE' }).catch(() => {});
        }

        if (!Object.keys(files).length) throw new Error('No files found in the repository.');

        // Step 2: merge files into the current project
        const mergeRes = await authFetch(`/api/projects/${projectId}/import-files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files }),
        });
        if (!mergeRes.ok) {
          const err = await mergeRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to import files into project.');
        }
        const mergeData = await mergeRes.json();
        onImported({ fileCount: Object.keys(files).length, changes: mergeData.changes });
      } else {
        // ── Create new project (home page flow) ───────────────────────────
        const name = tab === 'git' ? (_repoNameFromUrl(gitUrl) || 'Imported Project') : (zipFile?.name?.replace('.zip', '') || 'Imported Project');
        const data = tab === 'git'
          ? await importFromGitUrl(gitUrl.trim(), name)
          : await importFromZip(zipFile, name);
        onImported(data.project_id, data);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.zip')) { setZipFile(f); setError(''); }
    else setError('Only .zip files are accepted.');
  };

  return (
    <div style={s.wrap}>
      <h2 style={s.title}>📥 Import Repository</h2>
      <p style={s.subtitle}>
        {projectId
          ? 'Import an existing codebase into this project.'
          : 'Import an existing codebase to work on it with AI.'}
      </p>

      {/* Tabs */}
      <div style={s.tabs}>
        <button onClick={() => { setTab('git'); setError(''); }} style={{ ...s.tab, ...(tab === 'git' ? s.tabActive : {}) }}>
          🔗 Git URL
        </button>
        <button onClick={() => { setTab('zip'); setError(''); }} style={{ ...s.tab, ...(tab === 'zip' ? s.tabActive : {}) }}>
          📦 Upload ZIP
        </button>
        <button onClick={() => { setTab('oauth'); setError(''); }} style={{ ...s.tab, ...(tab === 'oauth' ? s.tabActive : {}) }}>
          🐙 GitHub / Bitbucket
        </button>
      </div>

      {/* Git URL tab */}
      {tab === 'git' && (
        <>
          <label style={s.label}>Git Repository URL</label>
          <input
            style={s.input}
            placeholder="https://github.com/owner/repo.git"
            value={gitUrl}
            onChange={e => setGitUrl(e.target.value)}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleImport()}
          />
          <p style={s.hint}>
            Public repos work directly. For private repos use:{' '}
            <code style={s.code}>https://&lt;token&gt;@github.com/owner/repo.git</code>
          </p>
        </>
      )}

      {/* ZIP tab */}
      {tab === 'zip' && (
        <>
          <label style={s.label}>ZIP File <span style={s.optional}>(max 100MB)</span></label>
          <div
            style={{ ...s.dropzone, ...(dragOver ? s.dropzoneActive : {}), ...(zipFile ? s.dropzoneDone : {}) }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current.click()}
          >
            {zipFile ? (
              <div style={s.fileInfo}>
                <span style={s.fileIcon}>📦</span>
                <div>
                  <p style={s.fileName}>{zipFile.name}</p>
                  <p style={s.fileSize}>{(zipFile.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
                <button style={s.clearBtn} onClick={e => { e.stopPropagation(); setZipFile(null); }}>✕</button>
              </div>
            ) : (
              <>
                <span style={s.dropIcon}>📂</span>
                <p style={s.dropText}>Drag & drop a <strong>.zip</strong> file here, or click to browse</p>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".zip" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; if (f) { setZipFile(f); setError(''); } }} />
        </>
      )}

      {/* OAuth tab */}
      {tab === 'oauth' && (
        <>
          <div style={s.providerRow}>
            {[{ id: 'github', icon: '🐙', label: 'GitHub' }, { id: 'bitbucket', icon: '🪣', label: 'Bitbucket' }].map(p => {
              const isActive = oauthProvider === p.id;
              const isDisabled = oauthProvider && oauthProvider !== p.id;
              return (
                <button
                  key={p.id}
                  disabled={isDisabled}
                  style={{ ...s.providerBtn, ...(isActive ? s.providerBtnActive : {}), ...(isDisabled ? s.providerBtnDisabled : {}) }}
                  onClick={() => !isDisabled && connectProvider(p.id)}
                >
                  <span style={{ fontSize: 22 }}>{p.icon}</span>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={s.providerLabel}>{p.label}</div>
                    <div style={s.providerStatus}>
                      {isActive ? 'Connected — viewing repos' : isDisabled ? 'Disconnect current to switch' : 'Click to connect'}
                    </div>
                  </div>
                  {isActive && <span style={s.connectedDot}>●</span>}
                </button>
              );
            })}
          </div>

          {oauthProvider && (
            <>
              <div style={s.searchRow}>
                <input
                  style={s.searchInput}
                  placeholder="Search repositories..."
                  value={repoSearch}
                  onChange={e => setRepoSearch(e.target.value)}
                />
                <button onClick={disconnectProvider} style={s.switchBtn}>
                  🔌 Disconnect
                </button>
              </div>
              {reposLoading ? (
                <div style={s.oauthCenter}>Loading repositories...</div>
              ) : repos.length === 0 ? (
                <div style={s.oauthCenter}>No repositories found</div>
              ) : (
                <div style={s.repoList}>
                  {repos
                    .filter(r => r.name.toLowerCase().includes(repoSearch.toLowerCase()))
                    .map(repo => (
                      <div key={repo.name} style={s.repoRow}>
                        <div style={s.repoInfo}>
                          <span style={s.repoName}>{repo.name}</span>
                          {repo.private && <span style={s.privateBadge}>🔒 Private</span>}
                        </div>
                        <button
                          style={{ ...s.primaryBtn, fontSize: 12, padding: '6px 14px', ...(importing === repo.name ? s.btnDisabled : {}) }}
                          disabled={!!importing}
                          onClick={() => handleOauthImport(repo)}
                        >
                          {importing === repo.name ? 'Importing...' : 'Import'}
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}

          {!oauthProvider && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Connect a provider to browse and import your repositories, including private ones.
            </p>
          )}
        </>
      )}

      {error && <p style={s.error}>⚠ {error}</p>}

      {loading && (
        <div style={s.loadingBox}>
          <span>⏳</span>
          <span style={s.loadingText}>
            {tab === 'git' ? 'Cloning repository...' : 'Extracting ZIP...'} This may take a moment.
          </span>
        </div>
      )}

      {tab !== 'oauth' && (
        <div style={s.actions}>
          <button onClick={handleImport} disabled={loading} style={{ ...s.primaryBtn, ...(loading ? s.btnDisabled : {}) }}>
            {loading ? 'Importing...' : '📥 Import'}
          </button>
          <button onClick={onCancel} disabled={loading} style={s.secondaryBtn}>Cancel</button>
        </div>
      )}
      {tab === 'oauth' && (
        <div style={s.actions}>
          <button onClick={onCancel} style={s.secondaryBtn}>Close</button>
        </div>
      )}
    </div>
  );
}

function _repoNameFromUrl(url) {
  try {
    return url.replace(/\.git$/, '').replace(/\/$/, '').split('/').pop() || '';
  } catch { return ''; }
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14, padding: 34, background: 'var(--bg-secondary)', borderRadius: 16, border: '1px solid var(--border)', flex: 1 },
  title: { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-muted)', margin: '-8px 0 4px' },
  tabs: { display: 'flex', gap: 8, padding: '4px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid var(--border)', width: 'fit-content' },
  tab: { padding: '7px 18px', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tabActive: { background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: -8 },
  optional: { fontWeight: 400, color: '#475569' },
  input: { padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  hint: { fontSize: 12, color: '#475569', margin: '-6px 0 0' },
  code: { background: 'var(--border)', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' },
  dropzone: { border: '2px dashed var(--border)', borderRadius: 10, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'all 0.2s', background: 'rgba(255,255,255,0.02)' },
  dropzoneActive: { borderColor: '#6366f1', background: 'rgba(99,102,241,0.06)' },
  dropzoneDone: { borderColor: '#22c55e', background: 'rgba(34,197,94,0.04)', flexDirection: 'row', justifyContent: 'flex-start', padding: '14px 20px' },
  dropIcon: { fontSize: 32, opacity: 0.4 },
  dropText: { fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' },
  fileInfo: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' },
  fileIcon: { fontSize: 24 },
  fileName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  fileSize: { fontSize: 12, color: 'var(--text-muted)', margin: 0 },
  clearBtn: { marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, padding: '2px 6px' },
  error: { fontSize: 13, color: '#f87171', background: 'rgba(248,113,113,0.08)', padding: '10px 14px', borderRadius: 8, margin: 0 },
  loadingBox: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(99,102,241,0.08)', borderRadius: 8, border: '1px solid rgba(99,102,241,0.2)' },
  loadingText: { fontSize: 13, color: '#a5b4fc' },
  actions: { display: 'flex', gap: 10, marginTop: 4 },
  primaryBtn: { padding: '11px 22px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  secondaryBtn: { padding: '11px 22px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  providerRow: { display: 'flex', gap: 12 },
  providerBtn: { flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' },
  providerBtnActive: { borderColor: '#6366f1', background: 'rgba(99,102,241,0.1)' },
  providerBtnConnected: { borderColor: 'rgba(34,197,94,0.4)' },
  providerBtnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
  providerLabel: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  providerStatus: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  searchRow: { display: 'flex', gap: 10, padding: '4px 0 8px', borderBottom: '1px solid var(--border)' },
  searchInput: { flex: 1, padding: '9px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none' },
  switchBtn: { padding: '9px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, color: '#f87171', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 },
  connectedDot: { color: '#22c55e', fontSize: 10, marginLeft: 'auto' },
  repoList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' },
  repoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, gap: 12 },
  repoInfo: { display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 },
  repoName: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  privateBadge: { fontSize: 10, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 },
  oauthCenter: { padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
};
